import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DeletePermissionDialogProps } from '../types';
import { entityConfig, permissionConfig } from '../constants/entityConfig';
import { getEntityName } from '../utils/entityUtils';

export const DeletePermissionDialog: React.FC<DeletePermissionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  permission,
  loading = false,
}) => {
  if (!permission) return null;

  const config = entityConfig[permission.entityType];
  const permissionInfo = permissionConfig[permission.permissionLevel];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#ffebee',
              color: '#f44336',
              width: 48,
              height: 48,
            }}
          >
            <WarningIcon />
          </Avatar>
          <Box>
            <Typography
              variant='h6'
              sx={{ fontWeight: 'bold', color: '#f44336' }}
            >
              Confirmar Eliminación
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Esta acción no se puede deshacer
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant='body1' gutterBottom>
            ¿Estás seguro de que quieres eliminar este permiso?
          </Typography>

          <Paper
            sx={{
              p: 2,
              mt: 2,
              bgcolor: 'background.default',
              border: `1px solid ${config?.color || '#ccc'}20`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Avatar
                sx={{
                  bgcolor: config?.bgColor || '#f5f5f5',
                  color: config?.color || '#666',
                  width: 32,
                  height: 32,
                }}
              >
                {config?.icon}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                  {getEntityName(permission)}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  {config?.name || permission.entityType}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Chip
                icon={permissionInfo?.icon}
                label={permissionInfo?.label || permission.permissionLevel}
                sx={{
                  bgcolor: permissionInfo?.bgColor || '#f5f5f5',
                  color: permissionInfo?.color || '#666',
                  fontWeight: 'bold',
                  height: 24,
                  '& .MuiChip-icon': {
                    color: permissionInfo?.color || '#666',
                  },
                }}
                size='small'
              />
              <Typography variant='caption' color='textSecondary'>
                • ID: {permission.entityId}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant='outlined'
          size='large'
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          color='error'
          size='large'
          startIcon={
            loading ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <DeleteIcon />
            )
          }
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Eliminando...' : 'Eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
